import type { ForcedSubtitleFilter, Subtitle, SubtitleEncoding, SubtitleFilter, SubtitlePosition } from '../types'

const LANGUAGE_NAMES: Record<string, string> = {
  ara: 'Arabic', bul: 'Bulgarian', ces: 'Czech', cze: 'Czech', dan: 'Danish',
  deu: 'German', dut: 'Dutch', ell: 'Greek', eng: 'English', fin: 'Finnish',
  fra: 'French', fre: 'French', ger: 'German', heb: 'Hebrew', hrv: 'Croatian',
  hun: 'Hungarian', ind: 'Indonesian', ita: 'Italian', jpn: 'Japanese', kor: 'Korean',
  nld: 'Dutch', nor: 'Norwegian', pol: 'Polish', por: 'Portuguese', pob: 'Portuguese (Brazil)',
  ron: 'Romanian', rum: 'Romanian', rus: 'Russian', slk: 'Slovak', slo: 'Slovak',
  spa: 'Spanish', srp: 'Serbian', swe: 'Swedish', tha: 'Thai', tur: 'Turkish',
  ukr: 'Ukrainian', vie: 'Vietnamese', zho: 'Chinese', chi: 'Chinese',
}

const LANGUAGE_ALIASES: Record<string, string> = {
  ar: 'ara', cs: 'ces', cze: 'ces', da: 'dan', de: 'deu', ger: 'deu',
  el: 'ell', en: 'eng', es: 'spa', fi: 'fin', fr: 'fra', fre: 'fra',
  he: 'heb', hr: 'hrv', hu: 'hun', id: 'ind', it: 'ita', ja: 'jpn',
  ko: 'kor', nl: 'nld', dut: 'nld', no: 'nor', pl: 'pol', pt: 'por',
  ro: 'ron', rum: 'ron', ru: 'rus', sk: 'slk', slo: 'slk', sr: 'srp',
  sv: 'swe', th: 'tha', tr: 'tur', uk: 'ukr', vi: 'vie', zh: 'zho', chi: 'zho',
}

export function canonicalLanguage(code: string) {
  const base = code.trim().toLowerCase().split(/[-_]/)[0]
  return LANGUAGE_ALIASES[base] || base
}

export function languageMatches(first: string, second: string) {
  return Boolean(first && second && canonicalLanguage(first) === canonicalLanguage(second))
}

/** Every language the app can name, for pickers that offer more than a shortlist. */
export function knownLanguages() {
  return Array.from(new Set(Object.values(LANGUAGE_ALIASES).concat(Object.keys(LANGUAGE_NAMES))))
    .filter((code) => LANGUAGE_NAMES[code])
    .sort((a, b) => LANGUAGE_NAMES[a].localeCompare(LANGUAGE_NAMES[b]))
}

export function languageName(code: string) {
  return LANGUAGE_NAMES[canonicalLanguage(code)] || code.toUpperCase()
}

/** Sort preferred language first and keep only a useful handful per language.
 * OpenSubtitles can return hundreds of duplicate files for one title. */
function subtitleFlags(item: Subtitle) {
  const text = `${item.id} ${item.url}`
  return {
    forced: /(?:^|[._\-/ ])forced(?:[._\-/ ]|$)/i.test(text),
    sdh: /(?:^|[._\-/ ])(?:sdh|cc|hearing[-_. ]?impaired)(?:[._\-/ ]|$)/i.test(text),
  }
}

export interface SubtitleRankOptions {
  forced?: ForcedSubtitleFilter
  sdh?: SubtitleFilter
}

export function rankSubtitles(subtitles: Subtitle[], preferred: string, perLanguage = 3, options: SubtitleRankOptions = {}) {
  const counts = new Map<string, number>()
  return subtitles
    .slice()
    .filter((item) => {
      const flags = subtitleFlags(item)
      if (options.forced === 'only' && !flags.forced) return false
      if (options.sdh === 'exclude' && flags.sdh) return false
      return true
    })
    .sort((a, b) => {
      const af = subtitleFlags(a)
      const bf = subtitleFlags(b)
      const forced = options.forced === 'prefer' ? Number(bf.forced) - Number(af.forced) : 0
      const sdh = options.sdh === 'prefer' ? Number(bf.sdh) - Number(af.sdh) : 0
      return Number(languageMatches(b.lang, preferred)) - Number(languageMatches(a.lang, preferred))
        || forced || sdh || languageName(a.lang).localeCompare(languageName(b.lang))
    })
    .filter((item) => {
      const count = counts.get(item.lang) || 0
      counts.set(item.lang, count + 1)
      return count < perLanguage
    })
}

/** HTML video understands WebVTT, while the add-on usually returns SRT. */
function shiftedTime(value: string, delaySeconds: number) {
  const match = value.match(/^(\d{1,2}):(\d{2}):(\d{2})[,.](\d{3})$/)
  if (!match) return value.replace(',', '.')
  const milliseconds = Math.max(0,
    ((Number(match[1]) * 3600 + Number(match[2]) * 60 + Number(match[3])) * 1000 + Number(match[4]))
      + Math.round(delaySeconds * 1000))
  const hours = Math.floor(milliseconds / 3_600_000)
  const minutes = Math.floor(milliseconds / 60_000) % 60
  const seconds = Math.floor(milliseconds / 1000) % 60
  const millis = milliseconds % 1000
  return `${String(hours).padStart(2, '0')}:${String(minutes).padStart(2, '0')}:${String(seconds).padStart(2, '0')}.${String(millis).padStart(3, '0')}`
}

function cuePosition(source: string, position: SubtitlePosition) {
  if (position === 'bottom') return source
  const line = position === 'top' ? '10%' : '50%'
  return source.replace(/^(\S+\s+-->\s+\S+)([^\n]*)$/gm, (_match, timing: string, settings: string) => (
    /(?:^|\s)line:/.test(settings) ? `${timing}${settings}` : `${timing}${settings} line:${line}`
  ))
}

export function srtToVtt(source: string, delaySeconds = 0, position: SubtitlePosition = 'bottom') {
  const clean = source.replace(/^\uFEFF/, '').replace(/\r\n?/g, '\n').trim()
  if (/^WEBVTT(?:\s|$)/i.test(clean)) {
    return `${cuePosition(delaySeconds
      ? clean.replace(/\d{1,2}:\d{2}:\d{2}[,.]\d{3}|(?<!\d)\d{2}:\d{2}[,.]\d{3}/g, (value) => {
        const normalized = /^\d{2}:\d{2}[,.]/.test(value) ? `00:${value}` : value
        const shifted = shiftedTime(normalized, delaySeconds)
        return /^00:/.test(normalized) && value.length < normalized.length ? shifted.slice(3) : shifted
      })
      : clean, position)}\n`
  }
  const body = clean
    .replace(/^(\d+)\s*\n(?=\d{1,2}:\d{2}:\d{2}[,.]\d{3}\s*-->)/gm, '$1\n')
    .replace(/\d{1,2}:\d{2}:\d{2}[,.]\d{3}/g, (value) => shiftedTime(value, delaySeconds))
    .replace(/\{\\([ibu])1\}/gi, '<$1>')
    .replace(/\{\\([ibu])0\}/gi, '</$1>')
    .replace(/\{\\[^}]+\}/g, '')
  return `WEBVTT\n\n${cuePosition(body, position)}\n`
}

export function decodeSubtitle(bytes: ArrayBuffer, encoding: SubtitleEncoding = 'auto') {
  const data = new Uint8Array(bytes)
  if (encoding === 'utf-8') return new TextDecoder('utf-8').decode(data)
  if (encoding === 'windows-1252') return decodeWindows1252(data)
  const utf8 = new TextDecoder('utf-8', { fatal: false }).decode(data)
  // Replacement characters are a strong signal that an old subtitle file is
  // Windows-1252. This repairs the common smart-quote/Central European case.
  return utf8.includes('\uFFFD') ? decodeWindows1252(data) : utf8
}

// Some older Chromium/ICU builds accept the windows-1252 label but decode its
// 0x80-0x9f range as ISO-8859-1 control characters. Map that range ourselves
// so smart quotes and Central European subtitle punctuation are deterministic.
const WINDOWS_1252: Array<number | undefined> = [
  0x20ac, undefined, 0x201a, 0x0192, 0x201e, 0x2026, 0x2020, 0x2021,
  0x02c6, 0x2030, 0x0160, 0x2039, 0x0152, undefined, 0x017d, undefined,
  undefined, 0x2018, 0x2019, 0x201c, 0x201d, 0x2022, 0x2013, 0x2014,
  0x02dc, 0x2122, 0x0161, 0x203a, 0x0153, undefined, 0x017e, 0x0178,
]

function decodeWindows1252(data: Uint8Array) {
  return Array.from(data, (byte) => {
    const mapped = byte >= 0x80 && byte <= 0x9f ? WINDOWS_1252[byte - 0x80] : undefined
    return String.fromCharCode(mapped ?? byte)
  }).join('')
}

/** A subtitle file is a few hundred KB at most. Anything larger is not one. */
const MAX_SUBTITLE_BYTES = 8 * 1024 * 1024

export async function subtitleBlobUrl(
  url: string,
  delaySeconds = 0,
  encoding: SubtitleEncoding = 'auto',
  position: SubtitlePosition = 'bottom',
  timeout = 20_000,
) {
  // Without this, a subtitle host that accepts the connection and never answers
  // leaves the picker spinning with no error and no way back.
  const controller = typeof AbortController !== 'undefined' ? new AbortController() : undefined
  const timer = setTimeout(() => controller?.abort(), timeout)
  try {
    const response = await fetch(url, { signal: controller?.signal })
    if (!response.ok) throw new Error(`Subtitle download failed (${response.status})`)
    const declared = Number(response.headers?.get?.('Content-Length') || 0)
    if (declared > MAX_SUBTITLE_BYTES) throw new Error('That subtitle file is too large to load.')
    const bytes = await response.arrayBuffer()
    if (bytes.byteLength > MAX_SUBTITLE_BYTES) throw new Error('That subtitle file is too large to load.')
    const text = decodeSubtitle(bytes, encoding)
    return URL.createObjectURL(new Blob([srtToVtt(text, delaySeconds, position)], { type: 'text/vtt' }))
  } catch (error) {
    if (error instanceof Error && error.name === 'AbortError') throw new Error('The subtitle provider timed out.')
    throw error
  } finally {
    clearTimeout(timer)
  }
}
